import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import "../css/Footer.css";
import "../css/FooterFx.css";
import "../css/FooterCraft.css";
import { SITE } from "../data/siteConfig";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const SERVICES = [
  "Wardrobes",
  "Kitchen Units",
  "TV Units",
  "Interior Panel Works",
  "Wooden Stairs",
  "All Customisation",
];

const WORDMARK = "ANAPPURAM".split("");

export default function Footer() {
  const year = new Date().getFullYear();
  const root = useRef(null);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Gold line draws across the top as the footer arrives
        gsap.fromTo(
          ".footer__topline",
          { scaleX: 0 },
          {
            scaleX: 1,
            ease: "none",
            scrollTrigger: {
              trigger: root.current,
              start: "top 98%",
              end: "top 60%",
              scrub: true,
            },
          },
        );

        // Columns rise in
        gsap.from(".footer__col", {
          y: 60,
          opacity: 0,
          duration: 0.9,
          stagger: 0.12,
          ease: "power3.out",
          clearProps: "transform,opacity",
          scrollTrigger: { trigger: ".footer__grid", start: "top 92%" },
        });

        // Social icons pop
        gsap.from(".footer__socials a", {
          scale: 0,
          rotate: -120,
          duration: 0.7,
          stagger: 0.1,
          ease: "back.out(2)",
          clearProps: "transform",
          scrollTrigger: { trigger: ".footer__socials", start: "top 95%" },
        });

        // Map curtain
        gsap.fromTo(
          ".footer__map",
          { clipPath: "inset(100% 0% 0% 0%)" },
          {
            clipPath: "inset(0% 0% 0% 0%)",
            duration: 1.2,
            ease: "power4.inOut",
            scrollTrigger: { trigger: ".footer__map", start: "top 98%" },
          },
        );

        // The plank slides in, then each letter is branded onto it:
        // it lands hot and glowing, then cools to charred brown.
        const brand = gsap.timeline({
          scrollTrigger: {
            trigger: ".footer__wordmark",
            start: "top 92%",
            once: true,
          },
        });
        brand
          .from(".footer__wordmark", {
            opacity: 0,
            y: 40,
            duration: 0.8,
            ease: "power3.out",
          })
          .fromTo(
            ".footer__wordmark .brand-letter",
            {
              opacity: 0,
              scale: 1.35,
              color: "#ffb04a",
              textShadow: "0 0 46px #ff7a1a, 0 0 10px #ff9a3a",
            },
            {
              opacity: 1,
              scale: 1,
              color: "#2a1a0e",
              textShadow:
                "0 1px 0 rgba(255,255,255,0.14), 0 0 0 rgba(255,122,26,0)",
              duration: 1.8,
              stagger: 0.16,
              ease: "power3.out",
              clearProps: "transform,color,textShadow", // lets the hover glow work
            },
            "-=0.3",
          )
          .from(
            ".footer__nail",
            { scale: 0, duration: 0.4, stagger: 0.1, ease: "back.out(3)" },
            0.4,
          );

        gsap.from(".footer__bottom-inner > *", {
          y: 20,
          opacity: 0,
          duration: 0.8,
          stagger: 0.12,
          ease: "power3.out",
          clearProps: "transform,opacity",
          scrollTrigger: { trigger: ".footer__bottom", start: "top 100%" },
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  // Page height changes between routes, so recalculate trigger positions
  useEffect(() => {
    const id = setTimeout(() => ScrollTrigger.refresh(), 400);
    return () => clearTimeout(id);
  }, [pathname]);

  const toTop = () =>
    gsap.to(window, { scrollTo: 0, duration: 1.4, ease: "power3.inOut" });

  return (
    <footer className="footer" ref={root}>
      <span className="footer__topline" aria-hidden="true" />

      <div className="container footer__grid">
        <div className="footer__col footer__brand">
          <div className="footer__logo">
            <span className="footer__logo-mark">A</span>
            <span>
              Anappuram
              <small>Wooden Furniture Work</small>
            </span>
          </div>
          <p>
            Custom wooden furniture and interiors from Palakkad, handcrafted
            since 1990 and delivered to customers all over India.
          </p>
          <div className="footer__socials">
            <a
              href="https://www.instagram.com/anappuram_furnitures?stkn=MXN5YXcxMjM0dWZmNQ=="
              aria-label="Instagram"
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path
                  fill="currentColor"
                  d="M12 8.4a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2zm0 5.94a2.34 2.34 0 1 1 0-4.68 2.34 2.34 0 0 1 0 4.68zM16.9 3H7.1A4.1 4.1 0 0 0 3 7.1v9.8A4.1 4.1 0 0 0 7.1 21h9.8a4.1 4.1 0 0 0 4.1-4.1V7.1A4.1 4.1 0 0 0 16.9 3zm2.83 13.9a2.83 2.83 0 0 1-2.83 2.83H7.1a2.83 2.83 0 0 1-2.83-2.83V7.1A2.83 2.83 0 0 1 7.1 4.27h9.8a2.83 2.83 0 0 1 2.83 2.83v9.8zm-.68-10.2a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
                />
              </svg>
            </a>
            <a
              href={SITE.whatsappLink}
              aria-label="WhatsApp"
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path
                  fill="currentColor"
                  d="M12.02 2.5c-5.25 0-9.5 4.25-9.5 9.5 0 1.68.44 3.25 1.2 4.62L2.5 21.5l5.03-1.18a9.46 9.46 0 0 0 4.49 1.14h.01c5.25 0 9.5-4.25 9.5-9.5s-4.26-9.46-9.51-9.46zm0 17.3h-.01a7.8 7.8 0 0 1-3.98-1.09l-.29-.17-2.98.7.71-2.9-.19-.3a7.78 7.78 0 0 1-1.19-4.14c0-4.31 3.51-7.82 7.83-7.82 2.09 0 4.05.82 5.53 2.3a7.77 7.77 0 0 1 2.29 5.53c0 4.31-3.51 7.79-7.71 7.79zm4.27-5.84c-.23-.12-1.38-.68-1.6-.76-.21-.08-.37-.12-.53.12-.16.23-.6.76-.74.91-.14.16-.27.18-.5.06-.23-.12-.98-.36-1.86-1.15-.69-.61-1.15-1.37-1.29-1.6-.14-.23-.02-.36.1-.48.11-.11.23-.27.35-.41.12-.14.16-.23.23-.39.08-.16.04-.29-.02-.41-.06-.12-.53-1.28-.73-1.75-.19-.46-.39-.4-.53-.4-.14 0-.29-.02-.45-.02-.16 0-.41.06-.63.29-.21.23-.83.81-.83 1.98s.85 2.29.97 2.45c.12.16 1.67 2.55 4.05 3.57.57.24 1.01.39 1.36.5.57.18 1.09.15 1.5.09.46-.07 1.38-.56 1.57-1.11.19-.54.19-1 .14-1.11-.06-.11-.21-.17-.44-.29z"
                />
              </svg>
            </a>
          </div>
        </div>

        <div className="footer__col">
          <h4>Quick Links</h4>
          <ul className="footer__links">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/about">About Us</Link>
            </li>
            <li>
              <Link to="/services">Services</Link>
            </li>
            <li>
              <Link to="/gallery">Gallery</Link>
            </li>
            <li>
              <Link to="/contact">Contact</Link>
            </li>
          </ul>
        </div>

        <div className="footer__col">
          <h4>Services</h4>
          <ul className="footer__links">
            {SERVICES.map((service) => (
              <li key={service}>
                <Link to="/services">{service}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="footer__col footer__contact">
          <h4>Get In Touch</h4>
          <ul>
            <li>
              Anappuram Wooden Furniture Work, Kinassery, Palakkad, Kerala
            </li>
            <li>
              <a href={SITE.phoneLink}>{SITE.phone}</a>
            </li>
            <li>
              <a href="mailto:anappuramfurniture@gmail.com">
                anappuramfurniture@gmail.com
              </a>
            </li>
          </ul>
          <div className="footer__map">
            <iframe
              title="Anappuram Wooden Furniture Work location map"
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d355.3568159036627!2d76.65590150533725!3d10.72218431714637!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTDCsDQzJzIwLjMiTiA3NsKwMzknMjEuNCJF!5e1!3m2!1sen!2sin!4v1790827667004!5m2!1sen!2sin"
              width="600"
              height="450"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      </div>

      {/* Wordmark branded onto a wooden plank */}
      <div className="footer__wordmark" aria-hidden="true">
        <i className="footer__nail footer__nail--l" />
        <i className="footer__nail footer__nail--r" />
        <div className="container">
          {WORDMARK.map((letter, i) => (
            <span className="brand-letter" key={`${letter}-${i}`}>
              {letter}
            </span>
          ))}
        </div>
      </div>

      <div className="footer__bottom">
        <div className="container footer__bottom-inner">
          <p>
            &copy; {year} Anappuram Wooden Furniture Work. All rights reserved.
          </p>
          <p>Handcrafted in Palakkad, Kerala since 1990</p>
          <button
            type="button"
            className="footer__top-btn"
            onClick={toTop}
            aria-label="Back to top"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 19V5M5 12l7-7 7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </footer>
  );
}
